/**
 * Vista 14 · Plantillas de documento.
 *
 * `ARQ-7`: las plantillas son **dato versionado, no código**. Cambiar el texto
 * de un oficio no requiere desplegar. Publicar añade una versión; la anterior
 * se conserva porque los documentos ya generados declaran con qué versión y
 * con qué hash salieron, y esa afirmación tiene que poder comprobarse.
 *
 * El hash es lo que hace verificable esa cadena, y por eso se muestra y se
 * puede copiar en lugar de quedarse en la base.
 */
import { useEffect, useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, StatusBadge } from '@mpfn/ui'
import {
  Callout,
  DataTable,
  FileUploadField,
  FormField,
  type DataTableColumn,
} from '@mpfn/patterns'
import { Copy, Download, Upload } from 'lucide-react'
import { api, BASE_API } from '../api/cliente.js'
import {
  useConfiguracionActa,
  useConfiguracionDocumentos,
  useGuardarConfiguracionActa,
  useGuardarConfiguracionDocumentos,
  type EstadoConfiguracionActa,
} from '../api/consultas.js'
import type { ConfiguracionActa, ConfiguracionDocumentos } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { instante } from '../lib/formato.js'

interface VersionPlantilla {
  codigo: string
  nombre: string
  version: number
  nombreArchivo: string
  hashSha256: string
  publicadoEn: string
  publicadoPor: string
}

interface EstadoPlantilla {
  codigo: string
  nombre: string
  vigente?: VersionPlantilla
  versiones: number
}

interface RespuestaPlantillas {
  plantillas: readonly EstadoPlantilla[]
  marcadoresMinimos: Readonly<Record<string, readonly string[]>>
  emisionBloqueada: boolean
}

const CLAVE = ['plantillas'] as const

export function Plantillas() {
  const plantillas = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.obtener<RespuestaPlantillas>('/plantillas'),
  })

  return (
    <Pagina
      titulo="Plantillas de documento"
      descripcion="Los formatos de las constancias e informes, y lo que imprimen el acta de selección de muestra, el oficio y los memorandos."
    >
      <Datos estado={plantillas}>{(datos) => <Contenido datos={datos} />}</Datos>
      <ConfiguracionDelActa />
      <ConfiguracionDeDocumentos />
    </Pagina>
  )
}

function Contenido({ datos }: { datos: RespuestaPlantillas }) {
  const [enPublicacion, setEnPublicacion] = useState<EstadoPlantilla | undefined>(undefined)

  const columnas: ReadonlyArray<DataTableColumn<EstadoPlantilla>> = [
    { id: 'nombre', header: 'Documento', cell: (fila) => fila.nombre },
    {
      id: 'version',
      header: 'Versión vigente',
      cell: (fila) => (fila.vigente ? `v${String(fila.vigente.version)}` : '—'),
    },
    {
      id: 'hash',
      header: 'Huella SHA-256',
      hideOnMobile: true,
      cell: (fila) =>
        fila.vigente ? <Huella valor={fila.vigente.hashSha256} /> : <span>Sin publicar</span>,
    },
    {
      id: 'publicado',
      header: 'Publicada',
      hideOnMobile: true,
      cell: (fila) =>
        fila.vigente ? `${instante(fila.vigente.publicadoEn)} · ${fila.vigente.publicadoPor}` : '—',
    },
    {
      id: 'estado',
      header: 'Estado',
      cell: (fila) =>
        fila.vigente ? (
          <StatusBadge tone="good" label={`${String(fila.versiones)} versiones`} />
        ) : (
          <StatusBadge tone="warning" label="Sin publicar" />
        ),
    },
    {
      id: 'acciones',
      header: '',
      align: 'right',
      cell: (fila) => (
        <span className="sigfisc-cola__acciones">
          {fila.vigente && (
            <Button variant="ghost" size="sm" asChild>
              <a href={`${BASE_API}/plantillas/${fila.codigo}/archivo`} download>
                <Download size={14} aria-hidden="true" />
                Descargar
              </a>
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEnPublicacion(fila)
            }}
          >
            <Upload size={14} aria-hidden="true" />
            Publicar versión
          </Button>
        </span>
      ),
    },
  ]

  return (
    <>
      {datos.emisionBloqueada && (
        <Callout tone="warning" title="La emisión productiva está bloqueada">
          Hasta aprobar las plantillas institucionales no se pueden emitir requerimientos en
          producción. El desarrollo y las pruebas no están afectados.
        </Callout>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Plantillas del sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columnas}
            rows={datos.plantillas}
            rowKey={(fila) => fila.codigo}
            empty={{ title: 'Sin plantillas' }}
          />
        </CardContent>
      </Card>

      {enPublicacion && (
        <Publicador
          plantilla={enPublicacion}
          marcadores={datos.marcadoresMinimos[enPublicacion.codigo] ?? []}
          onCerrar={() => {
            setEnPublicacion(undefined)
          }}
        />
      )}
    </>
  )
}

/** Las mismas que acepta el servidor: van en el número y en el nombre del PDF. */
const SIGLAS_VALIDAS = /^[A-Za-zÑñ0-9][A-Za-zÑñ0-9.-]*$/

/** El formulario trabaja con texto; los números se validan al enviar. */
interface CamposActa {
  siglas: string
  unidadEjecutora: string
  responsableNombre: string
  responsableCargo: string
  firmaIzquierda: string
  firmaDerecha: string
  ciudad: string
  anioNumeracion: string
  ultimoNumeroManual: string
}

function aCampos(configuracion: ConfiguracionActa): CamposActa {
  return {
    siglas: configuracion.siglas,
    unidadEjecutora: configuracion.unidadEjecutora,
    responsableNombre: configuracion.responsableNombre,
    responsableCargo: configuracion.responsableCargo,
    firmaIzquierda: configuracion.firmaIzquierda,
    firmaDerecha: configuracion.firmaDerecha,
    ciudad: configuracion.ciudad,
    anioNumeracion: configuracion.anioNumeracion !== undefined ? String(configuracion.anioNumeracion) : '',
    ultimoNumeroManual: String(configuracion.ultimoNumeroManual),
  }
}

/**
 * El acta del sorteo no es una plantilla DOCX: la genera el sistema con el
 * diseño del acta firmada de julio. Lo que imprime y no sale del sorteo —las
 * siglas del número, la unidad ejecutora, el responsable, las firmas— se
 * ajusta aquí, y cada cambio queda auditado.
 */
function ConfiguracionDelActa() {
  const estado = useConfiguracionActa()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Acta de selección de muestra</CardTitle>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <p className="sigfisc-nota">
          No es una plantilla: el sistema la genera con el diseño del acta firmada de julio de 2026,
          en borrador al generar la selección y numerada al confirmarla. Aquí se ajusta lo que
          imprime y no sale de la selección.
        </p>
        <Datos estado={estado}>{(datos) => <FormularioActa datos={datos} />}</Datos>
      </CardContent>
    </Card>
  )
}

function FormularioActa({ datos }: { datos: EstadoConfiguracionActa }) {
  const guardar = useGuardarConfiguracionActa()
  const [campos, setCampos] = useState<CamposActa>(() => aCampos(datos.configuracion))

  // Si otra pestaña guardó, el formulario se resincroniza en lugar de seguir
  // mostrando valores que ya no rigen.
  useEffect(() => {
    setCampos(aCampos(datos.configuracion))
  }, [datos.configuracion])

  const cambiar =
    (clave: keyof CamposActa) =>
    (evento: ChangeEvent<HTMLInputElement>): void => {
      const valor = evento.target.value
      setCampos((anteriores) => ({ ...anteriores, [clave]: valor }))
    }

  const ultimo = Number(campos.ultimoNumeroManual)
  const anio = campos.anioNumeracion.trim() === '' ? undefined : Number(campos.anioNumeracion)

  const errorSiglas = SIGLAS_VALIDAS.test(campos.siglas.trim())
    ? undefined
    : 'Solo letras, números, guiones y puntos.'
  const errorUltimo =
    campos.ultimoNumeroManual.trim() === '' || !Number.isInteger(ultimo) || ultimo < 0 || ultimo > 9999
      ? 'Un número entre 0 y 9999.'
      : undefined
  const errorAnio =
    anio !== undefined && (!Number.isInteger(anio) || anio < 2000 || anio > 2100)
      ? 'Un año entre 2000 y 2100.'
      : ultimo > 0 && anio === undefined
        ? 'Indique de qué año es esa numeración.'
        : undefined
  const faltaObligatorio = [
    campos.unidadEjecutora,
    campos.responsableCargo,
    campos.firmaIzquierda,
    campos.firmaDerecha,
    campos.ciudad,
  ].some((valor) => valor.trim() === '')
  const invalido =
    errorSiglas !== undefined || errorUltimo !== undefined || errorAnio !== undefined || faltaObligatorio

  return (
    <form
      className="sigfisc-formulario"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (invalido) return
        guardar.mutate(
          {
            siglas: campos.siglas.trim(),
            unidadEjecutora: campos.unidadEjecutora.trim(),
            responsableNombre: campos.responsableNombre.trim(),
            responsableCargo: campos.responsableCargo.trim(),
            firmaIzquierda: campos.firmaIzquierda.trim(),
            firmaDerecha: campos.firmaDerecha.trim(),
            ciudad: campos.ciudad.trim(),
            ...(anio !== undefined ? { anioNumeracion: anio } : {}),
            ultimoNumeroManual: ultimo,
          },
          {
            onSuccess: (respuesta) => {
              avisarExito(
                'Configuración del acta guardada.',
                `La próxima acta será la N.° ${respuesta.proximoNumero}.`,
              )
            },
            onError: (error) => {
              avisarError(error, 'No se pudo guardar la configuración del acta')
            },
          },
        )
      }}
    >
      <Callout tone="info" title={`La próxima acta será la N.° ${datos.proximoNumero}`}>
        El número sigue al último del año, lo haya emitido el sistema o se haya emitido a mano. Si
        ya existe un acta con ese número fuera del sistema, regístrela abajo para no repetirlo.
      </Callout>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField
          id="acta-siglas"
          label="Siglas del número"
          required
          hint="Lo que sigue a «0003-2026-» en el número del acta."
          error={errorSiglas ?? null}
        >
          <Input value={campos.siglas} onChange={cambiar('siglas')} />
        </FormField>
        <FormField id="acta-unidad" label="Unidad ejecutora" required>
          <Input value={campos.unidadEjecutora} onChange={cambiar('unidadEjecutora')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField
          id="acta-responsable"
          label="Responsable"
          hint="El nombre que imprime el acta. Vacío, imprime una raya."
        >
          <Input value={campos.responsableNombre} onChange={cambiar('responsableNombre')} />
        </FormField>
        <FormField id="acta-cargo" label="Cargo del responsable" required>
          <Input value={campos.responsableCargo} onChange={cambiar('responsableCargo')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField id="acta-firma-izquierda" label="Firma de la izquierda" required>
          <Input value={campos.firmaIzquierda} onChange={cambiar('firmaIzquierda')} />
        </FormField>
        <FormField id="acta-firma-derecha" label="Firma de la derecha" required>
          <Input value={campos.firmaDerecha} onChange={cambiar('firmaDerecha')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--3">
        <FormField id="acta-ciudad" label="Ciudad" required>
          <Input value={campos.ciudad} onChange={cambiar('ciudad')} />
        </FormField>
        <FormField
          id="acta-anio"
          label="Año de la numeración a mano"
          hint="El de las actas emitidas fuera del sistema."
          error={errorAnio ?? null}
        >
          <Input type="number" min={2000} max={2100} value={campos.anioNumeracion} onChange={cambiar('anioNumeracion')} />
        </FormField>
        <FormField
          id="acta-ultimo"
          label="Última acta emitida a mano"
          required
          hint="La de julio de 2026 es la N.° 0002."
          error={errorUltimo ?? null}
        >
          <Input
            type="number"
            min={0}
            max={9999}
            value={campos.ultimoNumeroManual}
            onChange={cambiar('ultimoNumeroManual')}
          />
        </FormField>
      </div>

      <Button variant="primary" type="submit" loading={guardar.isPending} disabled={invalido}>
        Guardar la configuración del acta
      </Button>
    </form>
  )
}

/**
 * El oficio y los memorandos tampoco son plantillas: el sistema los genera en
 * PDF con el formato de los documentos del área. Lo que imprimen y no sale del
 * expediente —las siglas, la dependencia, quién firma, a quién va el
 * memorando, el correo de la mesa de partes y la denominación del año— se
 * ajusta aquí.
 */
function ConfiguracionDeDocumentos() {
  const estado = useConfiguracionDocumentos()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Oficio y memorandos</CardTitle>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <p className="sigfisc-nota">
          El sistema los genera en PDF, sin número: se numeran y se firman en el trámite documentario y
          vuelven firmados. Aquí se ajusta lo que imprimen y no sale del expediente.
        </p>
        <Datos estado={estado}>{(datos) => <FormularioDocumentos vigente={datos} />}</Datos>
      </CardContent>
    </Card>
  )
}

function FormularioDocumentos({ vigente }: { vigente: ConfiguracionDocumentos }) {
  const guardar = useGuardarConfiguracionDocumentos()
  const [campos, setCampos] = useState<ConfiguracionDocumentos>(vigente)

  useEffect(() => {
    setCampos(vigente)
  }, [vigente])

  const cambiar =
    (clave: keyof ConfiguracionDocumentos) =>
    (evento: ChangeEvent<HTMLInputElement>): void => {
      const valor = evento.target.value
      setCampos((anteriores) => ({ ...anteriores, [clave]: valor }))
    }

  const errorSiglas = SIGLAS_VALIDAS.test(campos.siglas.trim()) ? undefined : 'Solo letras, números, guiones y puntos.'
  const errorCorreo = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(campos.correoMesaPartes.trim()) ? undefined : 'Escriba un correo válido.'
  const faltaObligatorio = [campos.dependencia, campos.firmanteCargo, campos.memorandoDestinatarioCargo, campos.ciudad].some(
    (valor) => valor.trim() === '',
  )
  const invalido = errorSiglas !== undefined || errorCorreo !== undefined || faltaObligatorio

  return (
    <form
      className="sigfisc-formulario"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (invalido) return
        const recortados = Object.fromEntries(
          Object.entries(campos).map(([clave, valor]) => [clave, valor.trim()]),
        ) as unknown as ConfiguracionDocumentos
        guardar.mutate(recortados, {
          onSuccess: () => {
            avisarExito('Configuración del oficio y los memorandos guardada.')
          },
          onError: (error) => {
            avisarError(error, 'No se pudo guardar la configuración')
          },
        })
      }}
    >
      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField
          id="documentos-siglas"
          label="Siglas del número"
          required
          hint="Lo que sigue al número y al año en el trámite documentario."
          error={errorSiglas ?? null}
        >
          <Input value={campos.siglas} onChange={cambiar('siglas')} />
        </FormField>
        <FormField id="documentos-dependencia" label="Dependencia que emite" required>
          <Input value={campos.dependencia} onChange={cambiar('dependencia')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--3">
        <FormField id="documentos-firmante" label="Quién firma" hint="Vacío, la firma lleva solo el cargo.">
          <Input value={campos.firmanteNombre} onChange={cambiar('firmanteNombre')} />
        </FormField>
        <FormField id="documentos-firmante-cargo" label="Cargo de quien firma" required>
          <Input value={campos.firmanteCargo} onChange={cambiar('firmanteCargo')} />
        </FormField>
        <FormField id="documentos-iniciales" label="Iniciales" hint="Al pie, como «ABC/def».">
          <Input value={campos.iniciales} onChange={cambiar('iniciales')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField id="documentos-destinatario" label="A quién va el memorando de resultados">
          <Input value={campos.memorandoDestinatarioNombre} onChange={cambiar('memorandoDestinatarioNombre')} />
        </FormField>
        <FormField id="documentos-destinatario-cargo" label="Su cargo" required>
          <Input value={campos.memorandoDestinatarioCargo} onChange={cambiar('memorandoDestinatarioCargo')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField
          id="documentos-correo"
          label="Correo de la mesa de partes"
          required
          hint="Adonde la entidad envía su respuesta."
          error={errorCorreo ?? null}
        >
          <Input type="email" value={campos.correoMesaPartes} onChange={cambiar('correoMesaPartes')} />
        </FormField>
        <FormField id="documentos-ciudad" label="Ciudad" required>
          <Input value={campos.ciudad} onChange={cambiar('ciudad')} />
        </FormField>
      </div>

      <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
        <FormField id="documentos-decenio" label="Denominación del decenio" hint="Va en el membrete. Vacía, no se imprime.">
          <Input value={campos.nombreDecenio} onChange={cambiar('nombreDecenio')} />
        </FormField>
        <FormField id="documentos-anio" label="Denominación del año" hint="Cambia cada año.">
          <Input value={campos.nombreAnio} onChange={cambiar('nombreAnio')} />
        </FormField>
      </div>

      <Button variant="primary" type="submit" loading={guardar.isPending} disabled={invalido}>
        Guardar la configuración del oficio y los memorandos
      </Button>
    </form>
  )
}

function Huella({ valor }: { valor: string }) {
  const [copiado, setCopiado] = useState(false)

  return (
    <span className="sigfisc-huella">
      <code title={valor}>{valor.slice(0, 12)}…</code>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Copiar la huella completa"
        onClick={() => {
          void navigator.clipboard.writeText(valor).then(() => {
            setCopiado(true)
            window.setTimeout(() => {
              setCopiado(false)
            }, 2000)
          })
        }}
      >
        <Copy size={14} aria-hidden="true" />
        {copiado ? 'Copiada' : ''}
      </Button>
    </span>
  )
}

function Publicador({
  plantilla,
  marcadores,
  onCerrar,
}: {
  plantilla: EstadoPlantilla
  marcadores: readonly string[]
  onCerrar: () => void
}) {
  const cliente = useQueryClient()
  const [archivo, setArchivo] = useState<File | null>(null)

  const publicar = useMutation({
    mutationFn: (elegido: File) => {
      const formulario = new FormData()
      formulario.append('archivo', elegido)
      return api.subir<{ plantilla: VersionPlantilla }>(
        `/plantillas/${plantilla.codigo}`,
        formulario,
      )
    },
    onSuccess: async (datos) => {
      avisarExito(
        `Publicada la versión ${String(datos.plantilla.version)} de ${plantilla.nombre}.`,
        `Huella ${datos.plantilla.hashSha256.slice(0, 12)}…`,
      )
      await cliente.invalidateQueries({ queryKey: CLAVE })
      onCerrar()
    },
    onError: (error) => {
      avisarError(error, 'No se pudo publicar la plantilla')
    },
  })

  const siguiente = (plantilla.vigente?.version ?? 0) + 1

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Publicar la versión {siguiente} de {plantilla.nombre}
        </CardTitle>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <Callout tone="info" title="Marcadores obligatorios">
          El archivo debe contener, al menos:{' '}
          {marcadores.map((nombre) => `{{${nombre}}}`).join(', ')}. Si falta alguno, la publicación
          se rechaza y el mensaje dice cuál.
        </Callout>

        <FormField id="plantilla-archivo" label="Archivo .docx" required>
          <FileUploadField
            id="plantilla-archivo"
            value={archivo}
            onChange={setArchivo}
            accept=".docx"
            acceptLabel="Documento de Word"
            maxSizeMb={20}
            variant="dropzone"
            onError={(mensaje) => {
              avisarError(new Error(mensaje), 'El archivo no es válido')
            }}
          />
        </FormField>

        <div className="sigfisc-dialogo__acciones">
          <Button variant="secondary" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={publicar.isPending}
            disabled={archivo === null}
            onClick={() => {
              if (archivo) publicar.mutate(archivo)
            }}
          >
            Publicar versión {siguiente}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
